/**
 * Framework-agnostic OCR-text → normalized-field extractor for Brazilian IDs.
 *
 * This module is intentionally DOM-free and I/O-free: it takes the raw text a
 * generic OCR pass produced plus the classifier's document-type key, and pulls
 * out the identity fields that document is expected to carry, normalising them
 * to a stable shape (CPF mask, ISO dates, collapsed whitespace).
 *
 * Honesty note: OCR over photographed IDs is noisy. Every extractor here is a
 * best-effort heuristic — it favours *not emitting* a field over emitting a
 * wrong one, so a missing key means "not confidently found", never "absent on
 * the document". None of these values are validated against check digits (the
 * one exception is the MRZ path, which reuses the verified TD1 parser).
 */
import { parseTd1, mrzToFields } from '../parsers/mrz/td1';

/** Collapse runs of whitespace and trim. */
function clean(s: string): string {
  return s.replace(/\s+/g, ' ').trim();
}

/** Strip accidental OCR label leftovers / stray punctuation around a value. */
function tidyValue(s: string): string {
  return clean(s)
    .replace(/^[\s:.\-–—|]+/, '')
    .replace(/[\s:.\-–—|]+$/, '')
    .trim();
}

/**
 * Normalise a CPF to `###.###.###-##`. Accepts any spacing/punctuation around
 * the 11 digits. Returns null if there aren't exactly 11 digits.
 */
export function normalizeCpf(raw: string): string | null {
  const digits = raw.replace(/\D/g, '');
  if (digits.length !== 11) return null;
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
}

/**
 * Normalise a Brazilian dd/mm/yyyy (or dd.mm.yyyy / dd-mm-yyyy, 2- or 4-digit
 * year) date to ISO `YYYY-MM-DD`. Returns null when it isn't a plausible date.
 */
export function normalizeDate(raw: string): string | null {
  const m = raw.match(/(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})/);
  if (!m) return null;
  let [, d, mo, y] = m;
  const day = Number(d);
  const mon = Number(mo);
  let year = Number(y);
  if (y.length === 2) year = year >= 30 ? 1900 + year : 2000 + year;
  if (day < 1 || day > 31 || mon < 1 || mon > 12) return null;
  if (year < 1900 || year > 2100) return null;
  const dd = String(day).padStart(2, '0');
  const mm = String(mon).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/**
 * Find the first CPF on the page. CPFs are masked as ###.###.###-## but some
 * documents separate the check digits with a slash (###.###.###/##), and OCR may
 * drop the punctuation, so we accept the masked forms first (either separator),
 * then fall back to a bare 11-digit run that isn't part of a longer number.
 */
function findCpf(text: string): string | null {
  const masked = text.match(/\b\d{3}\.\d{3}\.\d{3}[-/]\d{2}\b/);
  if (masked) return normalizeCpf(masked[0]);
  const bare = text.match(/(?<!\d)\d{11}(?!\d)/);
  return bare ? normalizeCpf(bare[0]) : null;
}

/** Lines, cleaned and non-empty, preserving order. */
function lines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map(clean)
    .filter(Boolean);
}

/**
 * Bilingual ID layouts (CIN, the new RG) print each field label in Portuguese AND English on the
 * same line — `NOME / NAME`, `NOME SOCIAL / SOCIAL NAME`, `FILIAÇÃO / PARENTS`. Everything after
 * that slash is still the LABEL, never the value: the value sits on the NEXT line.
 *
 * CPK-4199: without this check the text after the matched label (`/ NAME`) was returned as the
 * field value and adopted as the holder's legal name, so 170 clients ended up stored as "/ NAME"
 * or "/ SOCIAL NAME" and were then failed by Veriff/Serpro for a name mismatch we created.
 */
const BILINGUAL_LABEL_TAIL = /^\s*[/|]/;

/**
 * Pull the value that follows a labelled field. Looks for `LABEL: value` on the
 * same line, then (when the same line has nothing after the label) the next
 * non-empty line. `labels` are matched case-insensitively and accent-tolerantly.
 *
 * `isValid` (optional) is a shape check for the field's values. CPK-4341: OCR garbles the
 * bilingual label line's slash into other punctuation ("NOME / NAME" → "NOME ! NAME S"), so the
 * slash test alone can't tell label residue from a value — when the inline text fails the shape
 * check, the value is read from the next line instead (where bilingual layouts print it), and a
 * next line that also fails yields nothing rather than garbage.
 */
function valueForLabel(
  allLines: string[],
  labels: RegExp,
  isValid?: (v: string) => boolean,
): string | null {
  for (let i = 0; i < allLines.length; i++) {
    const line = allLines[i];
    const m = line.match(labels);
    if (!m) continue;
    const after = line.slice(m.index! + m[0].length);
    // A slash right after the label means we matched only the Portuguese half of a bilingual
    // label — treat the line as label-only so the value is read from the next line.
    const inline = BILINGUAL_LABEL_TAIL.test(after) ? '' : tidyValue(after);
    if (inline.length >= 2 && (!isValid || isValid(inline))) return inline;
    // Label sits alone on its line (or its tail was label residue/garble) → take the following line.
    const next = allLines[i + 1];
    if (next) {
      const nv = tidyValue(next);
      if (nv.length >= 2 && (!isValid || isValid(nv))) return nv;
    }
  }
  return null;
}

/** Accent-folded uppercase, for matching against the label vocabulary. */
function foldUpper(s: string): string {
  return s
    .toUpperCase()
    .replace(/[ÁÀÂÃÄ]/g, 'A')
    .replace(/[ÉÈÊË]/g, 'E')
    .replace(/[ÍÌÎÏ]/g, 'I')
    .replace(/[ÓÒÔÕÖ]/g, 'O')
    .replace(/[ÚÙÛÜ]/g, 'U')
    .replace(/Ç/g, 'C')
    .replace(/Ñ/g, 'N');
}

/**
 * Field-label vocabulary (pt + en) that a person's name is never made of. Used to reject a value
 * built ENTIRELY of label words — the residue of a bilingual label the OCR split badly, e.g.
 * "SOCIAL NAME" surviving from `NOME SOCIAL / SOCIAL NAME` (CPK-4199).
 */
const LABEL_WORDS = new Set([
  'NOME', 'NOMES', 'NAME', 'NAMES', 'SOCIAL', 'SOBRENOME', 'SURNAME', 'GIVEN', 'COMPLETO',
  'FULL', 'CIVIL', 'FILIACAO', 'PARENTS', 'PARENT', 'MAE', 'PAI', 'MOTHER', 'FATHER',
  'TITULAR', 'PORTADOR', 'HOLDER', 'ASSINATURA', 'SIGNATURE', 'DE', 'DO', 'DA', 'OF',
]);

/** Connector words that legitimately appear inside Brazilian names — never label evidence. */
const NAME_CONNECTORS = new Set(['DE', 'DO', 'DA', 'DAS', 'DOS', 'E', 'OF']);

/** A single name token: letters only, with internal apostrophes/hyphens (D'Ávila, Ana-Maria). */
const NAME_TOKEN = /^[A-Za-zÀ-ÿ]+(?:['’-][A-Za-zÀ-ÿ]+)*$/;

/** A name-like value: mostly letters/spaces, at least two words, and not just field labels. */
function looksLikeName(s: string): boolean {
  if (!/[A-Za-zÀ-ÿ]/.test(s)) return false;
  // A slash never belongs to a person's name — it means we captured part of a bilingual label
  // ("/ NAME") or a composite field ("CIDADE/UF") rather than the value. CPK-4199.
  if (s.includes('/')) return false;
  const letters = (s.match(/[A-Za-zÀ-ÿ]/g) ?? []).length;
  if (letters < s.replace(/\s/g, '').length * 0.7) return false;
  const tokens = s.trim().split(/\s+/);
  if (tokens.length < 2) return false;
  // CPK-4341: OCR garbles of the bilingual label line carry no slash but mix label words with
  // punctuation scraps and stray initials ("! Name S", "Name, Te", "(soc Name e"). Structural
  // rules, mirrored from the Textract path's scoreLine, reject them:
  //  - every token is letter-shaped (internal '/- allowed; no stray punctuation),
  //  - no single-letter token except the connector "E",
  //  - no token is a field-label word (a real name NEVER contains NOME/NAME/SOCIAL/…).
  if (!tokens.every((t) => NAME_TOKEN.test(t))) return false;
  const folded = tokens.map((t) => foldUpper(t));
  if (folded.some((t) => t.length === 1 && t !== 'E')) return false;
  // "NOMES" is exempt from the any-token rule: it exists as a real (rare) Brazilian surname, and
  // no observed corruption depends on it alone — the garbles always carry NOME/NAME/SOCIAL.
  if (folded.some((t) => LABEL_WORDS.has(t) && t !== 'NOMES' && !NAME_CONNECTORS.has(t))) return false;
  // Every token is a label/connector word → this is the label, not a person.
  if (folded.every((t) => LABEL_WORDS.has(t))) return false;
  return true;
}

/** Extract `nome` near a NOME label (handles "NOME E SOBRENOME" variants). */
function findNome(allLines: string[]): string | null {
  // The negative lookahead skips parent labels ("NOME DA MÃE" / "NOME DO PAI") — without it the
  // CPK-4341 next-line fallback could hand a parent's name back as the holder's.
  const v = valueForLabel(
    allLines,
    /\bnome(?:\s+e\s+sobrenome|\s+social|\s+completo)?\b(?!\s+d[ao]\b)\s*:?/i,
    looksLikeName,
  );
  if (v && looksLikeName(v) && !/^filiac|^m[aã]e|^pai/i.test(v)) return v;
  return null;
}

/** Extract filiação as a combined string, or the mãe/pai lines separately. */
function findFiliacao(allLines: string[]): { filiacao?: string; mae?: string; pai?: string } {
  const out: { filiacao?: string; mae?: string; pai?: string } = {};
  const mae = valueForLabel(allLines, /\b(?:m[aã]e|nome\s+da\s+m[aã]e)\b\s*:?/i, looksLikeName);
  const pai = valueForLabel(allLines, /\b(?:pai|nome\s+do\s+pai)\b\s*:?/i, looksLikeName);
  if (mae && looksLikeName(mae)) out.mae = mae;
  if (pai && looksLikeName(pai)) out.pai = pai;

  // A FILIACAO block: the two non-empty lines after the label are the parents.
  for (let i = 0; i < allLines.length; i++) {
    if (!/\bfiliac/i.test(allLines[i])) continue;
    const after = tidyValue(allLines[i].replace(/.*\bfiliac\w*\s*:?/i, ''));
    const parts: string[] = [];
    if (after && looksLikeName(after)) parts.push(after);
    for (let j = i + 1; j < allLines.length && parts.length < 2; j++) {
      const c = tidyValue(allLines[j]);
      if (looksLikeName(c)) parts.push(c);
      else if (parts.length) break;
    }
    if (parts.length) {
      out.filiacao = parts.join(' / ');
      if (!out.mae && parts[0]) out.mae = parts[0];
      if (!out.pai && parts[1]) out.pai = parts[1];
    }
    break;
  }
  return out;
}

/** Extract an RG / registro geral number (digits, may carry X and a dot/dash). */
function findRg(allLines: string[]): string | null {
  const v = valueForLabel(
    allLines,
    /\b(?:registro\s+geral|reg(?:istro)?\.?\s*ger\w*|identidade|rg|n[º°o]?\s*registro)\b\s*:?/i,
  );
  if (v) {
    const m = v.match(/[\d.\- ]{5,}[\dXx]?/);
    if (m) {
      const norm = tidyValue(m[0]);
      if (/\d{5,}/.test(norm.replace(/\D/g, '') + (/[Xx]$/.test(norm) ? '' : ''))) return norm;
    }
  }
  return null;
}

/** Extract naturalidade (place of birth) following its label. */
function findNaturalidade(allLines: string[]): string | null {
  const v = valueForLabel(allLines, /\bnaturalidade\b\s*:?/i);
  return v && /[A-Za-zÀ-ÿ]{3,}/.test(v) ? v : null;
}

/** Extract órgão expedidor / issuing authority following its label. */
function findOrgao(allLines: string[]): string | null {
  const v = valueForLabel(
    allLines,
    /\b(?:[oó]rg[aã]o\s+(?:expedidor|emissor)|expedidor|emissor)\b\s*:?/i,
  );
  return v && /[A-Za-zÀ-ÿ]{2,}/.test(v) ? v : null;
}

/** A date following a labelled field, normalised. */
function dateForLabel(allLines: string[], labels: RegExp): string | null {
  const v = valueForLabel(allLines, labels);
  return v ? normalizeDate(v) : null;
}

/**
 * Which fields each document type is expected to carry. Drives extraction so we
 * never surface, say, an órgão-expedidor guess on a CNH-front that has none.
 */
type FieldName =
  | 'nome'
  | 'cpf'
  | 'rg'
  | 'dataNascimento'
  | 'dataValidade'
  | 'dataEmissao'
  | 'naturalidade'
  | 'orgaoExpedidor'
  | 'filiacao';

const TYPE_FIELDS: Record<string, FieldName[]> = {
  'cnh-front': ['nome', 'cpf', 'rg', 'dataNascimento', 'dataValidade', 'dataEmissao', 'filiacao'],
  'cnh-open': ['nome', 'cpf', 'rg', 'dataNascimento', 'dataValidade', 'dataEmissao', 'filiacao'],
  'cnh-digital': ['nome', 'cpf', 'rg', 'dataNascimento', 'dataValidade', 'filiacao'],
  'rg-front': ['nome', 'rg', 'filiacao', 'dataNascimento', 'naturalidade', 'orgaoExpedidor'],
  'rg-back': ['cpf', 'rg', 'naturalidade', 'dataEmissao', 'orgaoExpedidor', 'filiacao'],
  'rg-open': [
    'nome',
    'cpf',
    'rg',
    'filiacao',
    'dataNascimento',
    'naturalidade',
    'orgaoExpedidor',
    'dataEmissao',
  ],
  'cin-front': ['nome', 'cpf', 'dataNascimento', 'filiacao'],
  'cin-back': ['cpf', 'naturalidade', 'orgaoExpedidor', 'dataEmissao'],
  'cin-open': ['nome', 'cpf', 'rg', 'dataNascimento', 'filiacao', 'naturalidade', 'orgaoExpedidor'],
  'cin-digital': ['nome', 'cpf', 'dataNascimento', 'filiacao'],
  certidaonasc: ['nome', 'cpf', 'dataNascimento', 'naturalidade', 'filiacao'],
  pescador: ['nome', 'cpf', 'rg', 'dataNascimento', 'orgaoExpedidor'],
  'pescador-back': ['cpf', 'rg', 'orgaoExpedidor'],
};

/** Fallback set when the type is unknown: try everything broadly applicable. */
const DEFAULT_FIELDS: FieldName[] = [
  'nome',
  'cpf',
  'rg',
  'dataNascimento',
  'dataValidade',
  'dataEmissao',
  'naturalidade',
  'orgaoExpedidor',
  'filiacao',
];

/**
 * Extract normalised identity fields from raw OCR `text`, tuned to the document
 * `type` (one of the classifier labels; unknown types fall back to a broad set).
 *
 * Returns a flat `Record<string,string>`. Keys present only when confidently
 * found. CPF is masked, dates are ISO `YYYY-MM-DD`. When the text contains a
 * TD1 MRZ (CIN), the verified MRZ parser is reused and its name/dates merged in.
 *
 * Best-effort by design — see the module header. Deterministic and DOM-free.
 */
export function extractFields(text: string, type: string): Record<string, string> {
  const out: Record<string, string> = {};
  if (!text || !text.trim()) return out;

  const wanted = new Set(TYPE_FIELDS[type] ?? DEFAULT_FIELDS);
  const allLines = lines(text);

  if (wanted.has('cpf')) {
    const cpf = findCpf(text);
    if (cpf) out['cpf'] = cpf;
  }
  if (wanted.has('nome')) {
    const nome = findNome(allLines);
    if (nome) out['nome'] = nome;
  }
  if (wanted.has('rg')) {
    const rg = findRg(allLines);
    if (rg) {
      out['rg'] = rg;
      out['registroGeral'] = rg;
    }
  }
  if (wanted.has('dataNascimento')) {
    const dn = dateForLabel(
      allLines,
      /\b(?:data\s+(?:de\s+)?nascimento|nascimento|nasc\.?|dt\.?\s*nasc)\b\s*:?/i,
    );
    if (dn) out['dataNascimento'] = dn;
  }
  if (wanted.has('dataValidade')) {
    const dv = dateForLabel(allLines, /\b(?:validade|val\.?|data\s+(?:de\s+)?validade)\b\s*:?/i);
    if (dv) out['dataValidade'] = dv;
  }
  if (wanted.has('dataEmissao')) {
    const de = dateForLabel(
      allLines,
      /\b(?:data\s+(?:de\s+)?(?:emiss[aã]o|expedi[cç][aã]o)|emiss[aã]o|expedi[cç][aã]o)\b\s*:?/i,
    );
    if (de) out['dataEmissao'] = de;
  }
  if (wanted.has('naturalidade')) {
    const nat = findNaturalidade(allLines);
    if (nat) out['naturalidade'] = nat;
  }
  if (wanted.has('orgaoExpedidor')) {
    const org = findOrgao(allLines);
    if (org) out['orgaoExpedidor'] = org;
  }
  if (wanted.has('filiacao')) {
    const fil = findFiliacao(allLines);
    if (fil.filiacao) out['filiacao'] = fil.filiacao;
    if (fil.mae) out['mae'] = fil.mae;
    if (fil.pai) out['pai'] = fil.pai;
  }

  // MRZ path (CIN cards): reuse the verified TD1 parser, never duplicate it.
  const mrz = parseTd1(text);
  if (mrz) {
    for (const f of mrzToFields(mrz)) {
      if (f.label === 'Name' && f.value && !out['nome'] && looksLikeName(f.value)) {
        out['nome'] = f.value;
      }
      if (f.label === 'Birth date' && !out['dataNascimento']) {
        const d = normalizeDate(f.value);
        if (d) out['dataNascimento'] = d;
      }
      if (f.label === 'Expiry date' && !out['dataValidade'] && wanted.has('dataValidade')) {
        const d = normalizeDate(f.value);
        if (d) out['dataValidade'] = d;
      }
    }
  }

  return out;
}
